import { useState } from 'react';
import { Banknote, Clock, CreditCard, Loader2, Plus, SlidersHorizontal } from 'lucide-react';

import { useCustomer } from '@/hooks/useCustomer';
import {
  useStripeMethods,
  useRemoveStripeMethod,
} from '@/hooks/useStripeMethods';
import { stripeMethodLabel } from '@/lib/stripe';
import { StripeAddMethodModal } from '@/components/StripeAddMethodModal';
import { StripeCardPurposeModal } from '@/components/StripeCardPurposeModal';

/**
 * Settings → Payment methods. The one home for a customer's saved cards/banks:
 * add, remove, and choose which method pays what — the default for invoice
 * payments and the method used for autopay. This is also where the empty
 * states on /pay and /autopay send a customer with nothing set up yet.
 */
export default function PaymentsTab() {
  const { data: ctx } = useCustomer();
  const methodsQuery = useStripeMethods(!!ctx);
  const methods = methodsQuery.data?.methods ?? [];
  const pending = methodsQuery.data?.pending ?? [];
  const remove = useRemoveStripeMethod();
  const [addOpen, setAddOpen] = useState(false);
  // The just-saved (or "Edit uses") card whose purpose picker is open.
  const [purposePmId, setPurposePmId] = useState<string | null>(null);
  // True only when opened right after a save → auto-default + auto-enroll rentals.
  const [purposeAutoDefault, setPurposeAutoDefault] = useState(false);

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-bold text-brix-ink">Payment methods</h2>
        <p className="text-base text-brix-muted mt-1 leading-relaxed max-w-xl">
          This is where you set up how you pay. Details are encrypted by Stripe and never stored on
          our servers.
        </p>
      </div>

      <div className="rounded-xl border border-brix-border bg-brix-surface-alt/50 p-4">
        <p className="text-base font-bold text-brix-ink">Setting up payments</p>
        <ol className="mt-2 list-decimal space-y-1.5 pl-5 text-[15px] leading-relaxed text-slate-700">
          <li><span className="font-semibold">Add a card or bank</span> below — you can save more than one.</li>
          <li>When you save it, choose <span className="font-semibold">what it's for</span> — product orders, cylinder rentals, or equipment rentals.</li>
          <li>Rentals are paid <span className="font-semibold">automatically (autopay)</span>; product autopay is optional.</li>
        </ol>
        <p className="mt-2.5 text-sm text-brix-muted">
          You can change what each card is for, or remove it, anytime — use <span className="font-semibold">Edit uses</span> on a card below.
          To pay an invoice now, use the <span className="font-semibold">Pay</span> page.
        </p>
      </div>

      {pending.length > 0 && (
        <div className="space-y-2">
          {pending.map((p) => (
            <div
              key={p.stripe_pm_id}
              className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3.5"
            >
              <p className="flex flex-wrap items-center gap-2 text-base font-bold text-amber-900">
                <Clock size={17} className="shrink-0" />
                {stripeMethodLabel(p)}
                <span className="rounded-md border border-amber-300 bg-amber-100 px-2 py-0.5 text-xs font-semibold">
                  Waiting to be confirmed
                </span>
              </p>
              <p className="mt-1.5 text-[15px] leading-relaxed text-amber-900">
                We sent a small test deposit to this account
                {p.microdeposits_arrive_on ? ` (expected by ${fmtArrive(p.microdeposits_arrive_on)})` : ''}.
                It arrives with a short code — enter that code and this bank is ready to use. We
                can't charge it until then.
              </p>
              {p.verification_url && (
                <a
                  href={p.verification_url}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-2.5 inline-flex rounded-lg bg-amber-900 px-3.5 py-2 text-sm font-bold text-white hover:bg-amber-800"
                >
                  Enter the code →
                </a>
              )}
            </div>
          ))}
        </div>
      )}

      {methodsQuery.isLoading ? (
        <p className="text-base text-brix-muted"><Loader2 size={15} className="mr-1.5 inline animate-spin" />Loading…</p>
      ) : methods.length === 0 ? (
        <div className="rounded-xl border border-dashed border-brix-border bg-brix-surface-alt/40 px-4 py-7 text-center">
          <p className="text-base text-slate-700">No payment methods saved yet.</p>
          <button
            type="button"
            onClick={() => setAddOpen(true)}
            className="mt-3 inline-flex items-center gap-2 rounded-xl bg-brix-primary px-5 py-3 text-base font-bold text-white hover:bg-brix-primary-deep"
          >
            <Plus size={17} /> Add a payment method
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {methods.map((m) => (
            <div
              key={m.stripe_pm_id}
              className="flex flex-wrap items-center gap-2.5 rounded-xl border border-brix-border bg-brix-surface px-4 py-3.5"
            >
              {m.kind === 'ach' ? (
                <Banknote size={18} className="shrink-0 text-brix-primary" />
              ) : (
                <CreditCard size={18} className="shrink-0 text-brix-primary" />
              )}
              <span className="text-base font-bold text-slate-900">{stripeMethodLabel(m)}</span>
              {m.is_default && (
                <span className="rounded-md border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700">
                  Main payer
                </span>
              )}
              {m.is_autopay && (
                <span className="rounded-md border border-blue-200 bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-700">
                  On autopay
                </span>
              )}
              <div className="ml-auto flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setPurposePmId(m.stripe_pm_id)}
                  className="inline-flex items-center gap-1.5 text-sm font-bold text-brix-primary hover:underline"
                >
                  <SlidersHorizontal size={15} /> Edit uses
                </button>
                <button
                  type="button"
                  disabled={remove.isPending}
                  onClick={() => {
                    if (window.confirm(`Remove ${stripeMethodLabel(m)}?`)) remove.mutate(m.stripe_pm_id);
                  }}
                  className="text-sm text-red-600 hover:underline disabled:opacity-40"
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
          <button
            type="button"
            onClick={() => setAddOpen(true)}
            className="inline-flex items-center gap-2 text-base font-bold text-brix-primary hover:underline"
          >
            <Plus size={17} /> Add another payment method
          </button>
        </div>
      )}

      {addOpen && (
        <StripeAddMethodModal
          onClose={() => {
            setAddOpen(false);
            // A bank saved pending verification closes through here, not
            // onSaved — refetch so its row appears without a page reload.
            methodsQuery.refetch();
          }}
          onSaved={(pmId) => {
            setAddOpen(false);
            methodsQuery.refetch();
            // Right after saving: default this card + auto-enroll rentals, then
            // show the purpose picker so they can change it.
            setPurposeAutoDefault(true);
            setPurposePmId(pmId);
          }}
        />
      )}

      {purposePmId && (
        <StripeCardPurposeModal
          stripePmId={purposePmId}
          autoDefault={purposeAutoDefault}
          onClose={() => {
            setPurposePmId(null);
            setPurposeAutoDefault(false);
            methodsQuery.refetch();
          }}
        />
      )}
    </div>
  );
}

/** "September 4" — the microdeposit's expected arrival, read as a plain date. */
function fmtArrive(iso: string): string {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString(undefined, {
    timeZone: 'UTC',
    month: 'long',
    day: 'numeric',
  });
}
